import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

export interface AddableMutualFund {
  id: number;
  stockid: number;
  title: string;
  body?: string;
  isin: string;
}

interface AddMutualFundToPortfolioModalProps {
  show: boolean;
  fund?: AddableMutualFund | null;
  searchable?: boolean;
  onHide: () => void;
  onSuccess?: (fund: AddableMutualFund) => void | Promise<void>;
}

const today = () => new Date().toISOString().split('T')[0];

const formatCurrency = (amount: number) => new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2
}).format(Number.isFinite(amount) ? amount : 0);

const AddMutualFundToPortfolioModal: React.FC<AddMutualFundToPortfolioModalProps> = ({
  show,
  fund,
  searchable = false,
  onHide,
  onSuccess
}) => {
  const { token } = useAuth();
  const [selectedFund, setSelectedFund] = useState<AddableMutualFund | null>(fund || null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<AddableMutualFund[]>([]);
  const [searching, setSearching] = useState(false);
  const [addInitialTransaction, setAddInitialTransaction] = useState(true);
  const [transaction, setTransaction] = useState({
    date: today(),
    txn_type: 'purchase',
    quantity: '',
    price: '',
    folio: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show) return;
    setSelectedFund(fund || null);
    setSearchQuery('');
    setSearchResults([]);
    setAddInitialTransaction(true);
    setTransaction({
      date: today(),
      txn_type: 'purchase',
      quantity: '',
      price: '',
      folio: '',
      notes: ''
    });
    setError('');
  }, [show, fund]);

  const handleSearch = async (event: React.FormEvent) => {
    event.preventDefault();
    const query = searchQuery.trim();
    if (!query) {
      setError('Enter a fund name or ISIN to search.');
      return;
    }

    setSearching(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/mutual-funds/`, {
        params: { search: query },
        headers: { Authorization: `Token ${token || localStorage.getItem('token') || ''}` }
      });
      const results = (response.data.results || []) as AddableMutualFund[];
      setSearchResults(results);
      setSelectedFund(results[0] || null);
      if (results.length === 0) setError('No mutual funds found for that search.');
    } catch (err: any) {
      console.error('Mutual fund search error:', err);
      setError(err?.response?.data?.error || 'Failed to search mutual funds.');
    } finally {
      setSearching(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedFund) {
      setError('Select a mutual fund before adding it to your portfolio.');
      return;
    }

    const quantity = Number(transaction.quantity);
    const price = Number(transaction.price);
    if (addInitialTransaction && (!Number.isFinite(quantity) || quantity <= 0 || !Number.isFinite(price) || price <= 0)) {
      setError('Enter valid units and NAV for the initial transaction.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload: Record<string, unknown> = {
        product_id: selectedFund.id,
        folio: transaction.folio,
        notes: transaction.notes
      };
      if (addInitialTransaction) {
        payload.date = transaction.date;
        payload.txn_type = transaction.txn_type;
        payload.quantity = quantity;
        payload.price = price;
      }

      await axios.post(`${API_BASE_URL}/mutual-funds/add/`, payload, {
        headers: { Authorization: `Token ${token || localStorage.getItem('token') || ''}` }
      });
      await onSuccess?.(selectedFund);
      onHide();
    } catch (err: any) {
      console.error('Add mutual fund to portfolio error:', err);
      setError(err?.response?.data?.error || err?.response?.data?.detail || 'Failed to add mutual fund to portfolio.');
    } finally {
      setSaving(false);
    }
  };

  const totalAmount = Number(transaction.quantity) * Number(transaction.price);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {selectedFund ? `Add ${selectedFund.title} to Portfolio` : 'Add Mutual Fund to Portfolio'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

        {searchable && (
          <Form onSubmit={handleSearch} className="mb-3">
            <Form.Group>
              <Form.Label>Find a mutual fund</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="search"
                  placeholder="Search by fund name or ISIN"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                />
                <Button type="submit" variant="outline-primary" disabled={searching}>
                  {searching ? <Spinner animation="border" size="sm" /> : 'Search'}
                </Button>
              </div>
            </Form.Group>
          </Form>
        )}

        {searchable && searchResults.length > 0 && (
          <Form.Group className="mb-3">
            <Form.Label>Select mutual fund</Form.Label>
            <Form.Select
              aria-label="Select mutual fund"
              value={selectedFund?.id || ''}
              onChange={(event) => setSelectedFund(searchResults.find((item) => item.id === Number(event.target.value)) || null)}
            >
              {searchResults.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.title} ({item.isin || 'No ISIN'})
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        {selectedFund && (
          <Alert variant="light" className="border mb-3">
            <strong>{selectedFund.title}</strong>
            <div className="small text-muted">
              AMFI: {selectedFund.stockid} · ISIN: {selectedFund.isin || 'Not available'}
            </div>
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Folio number (optional)</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter folio number"
              value={transaction.folio}
              onChange={(event) => setTransaction((previous) => ({ ...previous, folio: event.target.value }))}
            />
          </Form.Group>

          <Form.Check
            type="switch"
            id="add-initial-mutual-fund-transaction"
            className="mb-3"
            label="Add an initial transaction now"
            checked={addInitialTransaction}
            onChange={(event) => setAddInitialTransaction(event.target.checked)}
          />

          {addInitialTransaction && (
            <>
              <div className="row g-3">
                <Form.Group className="col-md-6">
                  <Form.Label>Transaction date</Form.Label>
                  <Form.Control
                    type="date"
                    value={transaction.date}
                    onChange={(event) => setTransaction((previous) => ({ ...previous, date: event.target.value }))}
                  />
                </Form.Group>
                <Form.Group className="col-md-6">
                  <Form.Label>Transaction type</Form.Label>
                  <Form.Select
                    value={transaction.txn_type}
                    onChange={(event) => setTransaction((previous) => ({ ...previous, txn_type: event.target.value }))}
                  >
                    <option value="purchase">Purchase</option>
                    <option value="sip">SIP</option>
                    <option value="redeem">Redeem</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="col-md-6">
                  <Form.Label>Units</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.001"
                    placeholder="Enter number of units"
                    value={transaction.quantity}
                    onChange={(event) => setTransaction((previous) => ({ ...previous, quantity: event.target.value }))}
                  />
                </Form.Group>
                <Form.Group className="col-md-6">
                  <Form.Label>NAV per unit (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.0001"
                    placeholder="Enter NAV per unit"
                    value={transaction.price}
                    onChange={(event) => setTransaction((previous) => ({ ...previous, price: event.target.value }))}
                  />
                </Form.Group>
              </div>
              {Number.isFinite(totalAmount) && totalAmount > 0 && (
                <Alert variant="info" className="mt-3 mb-3">
                  <strong>Total investment:</strong> {formatCurrency(totalAmount)}
                </Alert>
              )}
            </>
          )}

          <Form.Group className="mt-3">
            <Form.Label>Notes (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Add any notes..."
              value={transaction.notes}
              onChange={(event) => setTransaction((previous) => ({ ...previous, notes: event.target.value }))}
            />
          </Form.Group>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button type="button" variant="secondary" onClick={onHide}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={saving || !selectedFund}>
              {saving ? <><Spinner animation="border" size="sm" className="me-2" />Adding...</> : 'Add to Portfolio'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default AddMutualFundToPortfolioModal;
